import { Moment } from 'moment'

import { DefaultFile, SearchResult } from 'Core/interfaces'

import { Contact as DefaultContact } from './Contact'

export enum CompanySearchResultFields {
	searchById = 'searchById',
	searchByName = 'searchByName'
}
export enum TreatmentCategoriesSearchResultFields {
	searchByCode = 'searchByCode',
	searchByName = 'searchByName',
	searchByDescription = 'searchByDescription'
}

export enum CompanyCategoriesSearchResultFields {
	searchByCode = 'searchByCode',
	searchByName = 'searchByName'
}

export enum AccountCategoriesSearchResultFields {
	searchByCode = 'searchByCode',
	searchByName = 'searchByName'
}

export enum CompanyFilterFields {
	includeInactive = 'includeInactive',
	billingAgencies = 'billingAgencies'
}

export enum MOULabels {
	Document = 'document',
	Subject = 'subject',
	MouNumber = 'mouNumber',
	IssuedDate = 'issuedDate',
	ExpirationDate = 'expirationDate',
	ServiceSiteIds = 'serviceSiteIds',
	IsActive = 'isActive',
	ServiceSiteId = 'serviceSiteId'
}

export enum NotesFormFields {
	noteType = 'type',
	page = 'pages',
	notes = 'description',
	isActive = 'isActive'
}

export interface SearchByCity extends Entity {
	city: string
}

export interface SearchByState extends Entity {
	state: NameCode & {
		region: string | null
	}
}

export interface SearchByAddress extends Entity {
	addressLine1: string
	addressLine2: string
}

interface CompanyContactFlags {
	customerSurvey: boolean
}

export type LoopCallback<T> = (item: T, index: number, arr: T[], parentNode?: T) => void

export type OnDropEvent = (id: Key, parentId: Key, isServiceSite: boolean) => void

export declare namespace Company {
	interface Basic extends Entity {
		billingAgencies: Entity[]
		isActive: boolean
	}

	namespace Report {
		interface Basic {
			id: number
			reportId: number
			createDate: string
			reportName: string | null
			companyRestricted: boolean
		}
		interface Request extends Omit<Basic, 'id' | 'createDate' | 'reportName'> {}

		interface Form extends Omit<Request, 'reportId'> {
			reportId: number | null
		}
	}
	interface Detailed extends Basic {
		accountCategoryRequired: boolean
		affiliateCode: string | null
		comments: string | null
		companyCategoryRequired: boolean
		isCustomer: boolean
		isSupplier: boolean
		regulatoryTreatmentMethodRequired: boolean
		structureCode: string | null
		structureName: string | null
		trackTransactionPoundUpdates: boolean
		treatmentCategoryRequired: boolean
		isTurkish: boolean
		powerBiCharts: PowerBiChart[]
		isSchedulingUsed: boolean
		isEhsEnabled: boolean
	}

	interface PowerBiChart {
		id: number
		powerBiChartId: number
		powerBiChartCode: string
		powerBiChartDescription: string
	}

	namespace Form {
		interface Add extends Omit<Company.Detailed, 'id' | 'billingAgencies' | 'contacts'> {
			billingAgencies: LabelValue<number>[]
		}
		interface Edit
			extends Omit<
				Company.Detailed,
				'id' | 'billingAgencies' | 'powerBiCharts' | 'contacts' | 'isTurkish'
			> {
			powerBiCharts: LabelValue<number>[]
		}
	}

	interface Request
		extends Omit<
			Company.Detailed,
			'id' | 'billingAgencies' | 'powerBiCharts' | 'contacts' | 'isTurkish'
		> {
		billingAgencyIds: number[]
		powerBiChartIds: number[]
	}

	namespace Search {
		interface Result extends SearchResult {}
		interface ExpandedResult extends Result {
			searchByCity: SearchByCity[]
			searchByState: SearchByState[]
			searchByAddress: SearchByAddress[]
		}
	}

	namespace TreatmentCategory {
		interface Basic extends Entity {
			isActive: boolean
			code: string | null
			description: string | null
		}

		interface Request extends Omit<Basic, 'id'> {}

		interface Form extends Request {}

		namespace Search {
			interface Result {
				[TreatmentCategoriesSearchResultFields.searchByCode]: Entity[]
				[TreatmentCategoriesSearchResultFields.searchByName]: Entity[]
				[TreatmentCategoriesSearchResultFields.searchByDescription]: Entity[]
			}
		}
	}

	namespace CompanyCategory {
		interface Basic {
			id: number
			categoryName: string | null
			categoryCode: string | null
			categoryName2: string | null
			categoryCode2: string | null
			categoryDescription: string | null
			isActive: boolean
		}
		interface Request extends Omit<Basic, 'id'> {}

		interface Form extends Request {}

		namespace Search {
			interface Result {
				[CompanyCategoriesSearchResultFields.searchByCode]: Entity[]
				[CompanyCategoriesSearchResultFields.searchByName]: Entity[]
			}
		}
	}

	namespace AccountCategory {
		interface Basic extends Entity {
			code: string | null
			isActive: boolean
			code2: string | null
			name2: string | null
			description: string | null
		}

		interface Detailed extends Basic {
			approverContact?: Entity
			backupContact?: Entity
			mainContact?: Entity
		}

		interface Request extends Omit<Basic, 'id'> {
			mainContactId?: string
			approverContactId?: string
			backupContactId?: string
		}

		namespace Search {
			interface Result {
				[AccountCategoriesSearchResultFields.searchByCode]: Entity[]
				[AccountCategoriesSearchResultFields.searchByName]: Entity[]
			}
		}

		interface Form extends Omit<Basic, 'id'> {
			mainContactId: LabelValue | null
			approverContactId: LabelValue | null
			backupContactId: LabelValue | null
		}
	}

	namespace Contact {
		interface Form extends DefaultContact.Form.Create.Basic, CompanyContactFlags {}
		namespace Request {
			interface Add {
				isActive: boolean
				survey: boolean
				contactId: number | string
			}
			interface Create extends DefaultContact.Form.Create.Request, CompanyContactFlags {}
		}

		interface Basic extends DefaultContact.Search.Result, Request.Add {}
	}

	namespace BillingAgencies {
		interface Form {
			billingAgencies: LabelValue<number>[]
		}
	}

	namespace Hierarchy {
		interface Basic extends Entity {
			isServiceSite: boolean
			children: Hierarchy.Basic[] | null
		}
		interface Formatted {
			key: number
			title: string
			className?: string
			isServiceSite: boolean
			children?: Formatted[]
			level: number
		}

		namespace Move {
			interface Request {
				levelId: Key
				corporateCompanyId: Key
				isServiceSite: boolean
			}
		}

		namespace Level {
			interface Contact {
				id?: number
				contactId: number
				isMainContact: boolean
				isActive: boolean
				contactName: string
			}
			interface Basic {
				id?: number
				name: string
				code: string
				corporateCompanyId: number | null
				contacts: Contact[]
			}
		}
	}

	namespace MOU {
		interface Basic {
			id: number
			fileName: string | null
			subject: string
			mouNumber: string
			issuedDate: string
			expirationDate: string
			isActive: boolean
			serviceSites: Entity[]
			hasPrimaryDocument: boolean
		}

		namespace Form {
			interface Generic {
				[MOULabels.Document]: DefaultFile | null
				[MOULabels.Subject]: string
				[MOULabels.MouNumber]: string
				[MOULabels.IssuedDate]: Moment | null
				[MOULabels.ExpirationDate]: Moment | null
				[MOULabels.ServiceSiteIds]: LabelValue<number>[]
				[MOULabels.IsActive]: boolean
				[MOULabels.ServiceSiteId]: LabelValue<number> | null
			}
		}

		namespace Request {
			interface Generic {
				[MOULabels.Document]: File | null
				[MOULabels.Subject]: string
				[MOULabels.MouNumber]: string
				[MOULabels.IssuedDate]: string
				[MOULabels.ExpirationDate]: string
				[MOULabels.ServiceSiteIds]: number[]
				[MOULabels.IsActive]: boolean
			}
		}
	}

	namespace ContractDetails {
		interface Basic {
			id: number
			companyId: number
			title: BasicString
			executionDate: BasicString
			customerParty: BasicString
			heritageParty: BasicString
			contractTerm: BasicString
			initialContractStartDate: BasicString
			currentContractStartDate: BasicString
			currentContractEndDate: BasicString
			terminationNotes: BasicString
			renewalTerms: BasicString
			autoRenewalEnabled: boolean
			autoRenewalTerms: BasicString
			optionalExtensionIncluded: boolean
			extensionEndDate: BasicString
			savingsTerms: BasicString
			savingsGuaranteed: boolean
			additionalSavingsGoals: boolean
			annualPriceIncreaseNotes: BasicString
			annualPriceIncreaseAllowed: boolean
			annualPriceIncreaseTerms: BasicString
			annualPriceIncreaseIndustry: BasicString
			annualPriceIncreaseCapped: boolean
			implementationFeeSpecified: boolean
			ongoingManagementFeeSpecified: boolean
			landfillReductionSpecified: boolean
			landfillFreeGoalSpecified: boolean
			otherRequirements: BasicString
			sustainabilityGoalsEnabled: boolean
			sustainabilityGoalsDescription: BasicString
			additionalContractNotes: BasicString
			siteLandfillFreeGoalSpecified: boolean
			isAbsoluteSpend: boolean
			contractSavings: {
				id: number
				goal: BasicNumber
				startDate: BasicString
				endDate: BasicString
			}[]
		}

		namespace Form {
			interface Generic
				extends Omit<
					Basic,
					| 'id'
					| 'companyId'
					| 'title'
					| 'initialContractStartDate'
					| 'currentContractStartDate'
					| 'currentContractEndDate'
					| 'extensionEndDate'
					| 'contractSavings'
				> {
				title: LabelValue | null
				initialContractStartDate: Moment | null
				currentContractStartDate: Moment | null
				currentContractEndDate: Moment | null
				extensionEndDate: Moment | null
				contractSavings: {
					id: number
					isEditable: boolean
					goal: BasicString
					startDate: Nullable<Moment>
					endDate: Nullable<Moment>
				}[]
			}

			interface Saving {
				id: number
				goal: BasicString
				startDate: Nullable<Moment>
				endDate: Nullable<Moment>
				isEditable: boolean
			}

			type SavingField = Record<keyof Saving, boolean | string | Moment | null>
		}

		namespace Request {
			interface Generic extends Omit<Basic, 'id' | 'companyId' | 'contractSavings'> {
				contractSavings: {
					id: number
					goal: number
					startDate: string
					endDate: string
				}[]
			}
		}
	}
	namespace PaymentDetails {
		interface Basic {
			id: number
			companyId: number
			paymentTerms: BasicString
			paymentTermDays: number | null
			paymentDateType: BasicString
			lateFee: number | null
			pricingFeeTerms: BasicString
			indemnificationTerms: BasicString
			transparency: BasicString
			spendRebateExist: boolean
			spendIncentiveRebate: BasicString
		}

		namespace Form {
			interface Generic extends Omit<Basic, 'id' | 'companyId'> {}
		}
		namespace Request {
			interface Generic extends Form.Generic {}
		}
	}

	namespace CustomerGeneralInfo {
		interface Basic {
			corporateCulture: BasicString
			corporateEscalationProcess: BasicString
			segmentation: BasicString
			businessType: BasicString
			contractType: BasicString
			agingCollectionsNotes: BasicString
			poNotes: BasicString
			reportingKpis: BasicString
			visitsCustomerExpectationsNotes: BasicString
			whatDoesCustomerDo: BasicString
			customerHistory: BasicString
		}
		namespace Form {
			interface Generic extends Basic {}
		}
		namespace Request {
			interface Generic extends Basic {}
		}
	}

	namespace Notes {
		interface Basic {
			id: number
			companyId: number
			[NotesFormFields.noteType]: Entity
			[NotesFormFields.page]: Entity[]
			[NotesFormFields.notes]: string
			[NotesFormFields.isActive]: boolean
		}

		interface BasicToDisplayOnPages {
			id: number
			type: string
			page: string
			description: string
		}

		namespace Form {
			interface Generic {
				[NotesFormFields.noteType]: LabelValue<number> | null
				[NotesFormFields.page]: LabelValue<number>[] | null
				[NotesFormFields.notes]: BasicString
				[NotesFormFields.isActive]: boolean
			}
		}
		namespace Request {
			interface Generic {
				[NotesFormFields.noteType]: number
				[NotesFormFields.page]: number[]
				[NotesFormFields.notes]: string
				[NotesFormFields.isActive]: boolean
			}
		}
	}

	namespace Convention {
		interface Basic {
			id: number
			companyId: number
			conventionField: Entity
			description: string
		}

		namespace Form {
			interface Generic {
				conventionFieldId: LabelValue<number> | null
				description: string
			}
		}
		namespace Request {
			interface Generic {
				conventionFieldId: number
				description: string
			}
		}
	}
}

export enum PagesForDisplayingCompanyNotes {
	itemPreview = 'item-preview',
	itemEdit = 'item-edit',
	serviceLocationPreview = 'service-location-preview',
	serviceLocationEdit = 'service-location-edit',
	wastestreamPreview = 'wastestream-preview',
	wastestreamEdit = 'wastestream-edit',
	kpiTracking = 'item-edit/kpi-tracking'
}

export enum PagesForDisplayingNameConvention {
	item = 'item',
	serviceLocation = 'service-location',
	wastestream = 'wastestream',
	treatmentMethod = 'item/treatment-method'
}
